// Mobx
import { action, computed, observable, reaction, autorun } from "mobx";

// Vue
import Vue from "vue";

// Api
import { Api } from "#/api";

// RootStore
import { Store } from "#/store";

// Enums
import { Language } from "@mgs/ci2019-common/src/enums/Language";

// Interfaces
import { AppTranslation } from "#/common/interfaces/AppTranslation";
import { IrisMessage } from "@mgs/ci2019-common/src/interfaces/IrisMessage";

export class UIState {
  // RootStore
  public rootStore: Store;

  // IE
  @observable
  public isIE11: boolean = false;

  // Login Status
  @observable
  public isLoggedIn: boolean = false;

  // === Language ===
  @observable
  public appLanguages: Map<Language, AppTranslation> = new Map();

  @observable
  public appCurrentLanguage: Language = Language.DE;

  @observable
  public appCurrentCountry: string = "GLOBAL";

  @observable
  public hasEntryLanguage: boolean = false;

  // === Favorites ===

  @observable
  public activeFlyout: string = "";

  // === Loader ===
  @observable
  public willLoadingBeHandled: boolean = false;

  @observable
  public isLoading: boolean = false;

  @observable
  public isNoScrollMode: boolean = false;

  @observable
  public isLoadingAuto: boolean = true;

  @observable
  public isLoadingSuccess: boolean = true;

  @observable
  public activeQueue: number = 0;

  // === PrivacyPolicy ===
  @observable
  public isPrivacyPolicyVisible: boolean = false;

  @observable
  public privacyPolicyActiveTab: string = "anbieter";

  // === Router ===
  @observable
  public routeName: string = "";

  @observable
  public routeTitleKey: string = "";

  // === Messages ===
  @observable
  public messages: IrisMessage[] = [];

  @observable
  public bodyPadding: number = 0;

  public constructor(store: Store) {
    this.rootStore = store;

    reaction(() => this.isNoScrollMode, this.onScrollModeChanged);

    // reaction(() => ({
    //         hasEntryLanguage: this.hasEntryLanguage,
    //         routeName: this.routeName,
    //         routeTitleKey: this.routeTitleKey
    //     }), (data) => {

    //     if (data.hasEntryLanguage) {

    //         const currentLanguageKeys = this.appLanguages.get(this.appCurrentLanguage)!
    //         document.title = currentLanguageKeys[data.routeTitleKey]
    //     }
    // })
  }

  // === Urls ===
  @computed
  public get languageApiUrl() {
    return `${APP_API_URL}language/v1.0/`;
  }

  @computed
  public get appApiUrl() {
    return `${APP_API_URL}cms/${this.appCurrentLanguage}/${this.appCurrentCountry}/`;
  }

  @computed
  public get warenkorbApiUrl() {
    return `${APP_API_URL}warenkorb/${this.appCurrentLanguage}/${this.appCurrentCountry}/`;
  }

  @computed
  public get conversionWorldApiUrl() {
    return `${APP_API_URL}conversionworld/${this.appCurrentLanguage}/${this.appCurrentCountry}/`;
  }

  @computed
  public get kpiApiUrl() {
    return `${APP_API_URL}kpi/v1.0/${this.appCurrentLanguage}/`;
  }

  @computed
  public get authAppUrl() {
    return `${APP_API_URL}auth/v2.0/${this.appCurrentLanguage}/`;
  }

  @computed
  public get identityApiUrl() {
    return `${APP_API_URL}identity/v1.0/${this.appCurrentLanguage}/`;
  }

  @computed
  public get ansprechpartnerApiUrl() {
    return `${APP_API_URL}ansprechpartner/`;
  }

  @computed
  public get katalogApiUrl() {
    return `${APP_API_URL}katalog/v1.0/${this.appCurrentLanguage}/`;
  }

  @computed
  public get authApiUrl() {
    return `${APP_API_URL}auth/v2.0/${this.appCurrentLanguage}/`;
  }

  @computed
  public get privacyPolicyUrl() {
    // return `${APP_API_URL}/${this.appCurrentLanguage}/`
    return `https://bb-integr2portal.mercedes-benz.com/${this.appCurrentLanguage}/`;
  }

  /* @computed
  public get appID() {
    return APP_MAPS_APP_ID;
  }

  @computed
  public get appCode() {
    return APP_MAPS_APP_CODE;
  } */

  @computed
  public get appApiKey() {
    return APP_MAP_API_KEY || "${HERE_MAP_API_KEY}"; // for env variables replace bash script
  }

  @action.bound
  public setAppCurrentCountry(country: string) {
    this.appCurrentCountry = country;
  }

  @action.bound
  public setAppCurrentLanguage(language: Language) {
    this.appCurrentLanguage = language;
  }

  @action.bound
  public setAppLanguage(payload: { language: Language; messages: any }) {
    this.appLanguages.set(payload.language, payload.messages);
  }

  @action.bound
  public async getLanguage(language: Language) {
    const messages = await Api.appUbersetzungService.getAll(language, [
      "cms_modules",
      "common",
    ]);
    this.setAppLanguage({ language: language, messages });

    return messages;
  }

  private onScrollModeChanged(isNoSroll) {
    if (isNoSroll) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
  }

  // === Messages ===
  @action.bound
  public success(message?: string, component?: Vue) {
    this.messages.push({
      type: "success",
      id: Date.now().toString(),
      message,
      component,
    });
  }

  @action.bound
  public error(message?: string, component?: Vue) {
    this.messages.push({
      type: "error",
      id: Date.now().toString(),
      message,
      component,
    });
  }

  @action.bound
  public info(message?: string, component?: Vue) {
    this.messages.push({
      type: "info",
      id: Date.now().toString(),
      message,
      component,
    });
  }

  @action.bound
  public closeMessage(id: string) {
    const messageIndex = this.messages.findIndex((message) => message.id == id);

    if (messageIndex != -1) {
      this.messages.splice(messageIndex, 1);
    }
  }

  @action.bound
  public closeAllMessages() {
    this.messages = [];
  }

  @action.bound
  public setShowFilter(show: boolean) {
    this.isNoScrollMode = show;
  }

  @action.bound
  public setShowProductComparison(show: boolean) {
    this.isNoScrollMode = show;
  }

  @action.bound
  startLoadingAnimation() {
    this.isLoadingAuto = false;
    this.isLoading = true;
  }

  @action.bound
  finishLoadingAnimation(success = true) {
    this.isLoadingSuccess = success;
    this.isLoading = false;
    this.isLoadingAuto = true;
    return success;
  }
}
